import type { EnhancementSettings } from "../types";
import {
  applyCompactMode,
  applyRowHighlights,
  clearRowHighlights,
  findScreenerTable,
  injectScoreColumn,
  removeScoreColumn,
  syncNoReportRowMarkers,
} from "./table";
import { syncRubricBandFilter } from "./rubricFilter";
import {
  openProxySettingsModal,
  startProxyHealthPolling,
  type ProxyHealthResult,
  type ProxyHealthStatus,
} from "./panel";
import {
  bindStockForgeAuthToolbar,
  checkStockForgeConnection,
  stockForgeAuthToolbarHtml,
  type StockForgeConnectionResult,
} from "./stockForgeAuth";

export interface ToolbarCallbacks {
  onSettingsChange: (settings: EnhancementSettings) => void;
  /** When set (web app), shows Stock Forge sign-in control next to proxy health. */
  stockForgeAuthCheck?: () => Promise<{ account_id?: string }>;
  /** Hide Stock Forge + proxy pills (e.g. when mounted in the web tab bar). */
  hideConnectionIndicators?: boolean;
}

export function proxyHealthIndicatorHtml(opts: { compact?: boolean; label?: string } = {}): string {
  const label = opts.label ?? "API proxy health";
  const msg = opts.compact
    ? ""
    : `<span class="sfe-proxy-health-msg" data-proxy-msg>—</span>`;
  return `
    <button type="button" class="sfe-proxy-health" data-action="proxy-health" data-tip="API proxy — checking… Click to configure" title="API proxy health — click to configure">
      <span class="sfe-proxy-dot" data-proxy-dot data-status="unknown" aria-hidden="true"></span>
      <span class="sfe-proxy-health-label">${label}</span>
      ${msg}
    </button>
  `;
}

export function createToolbar(
  settings: EnhancementSettings,
  callbacks: ToolbarCallbacks,
): HTMLElement {
  const root = document.createElement("div");
  root.className = "sfe-toolbar";
  root.innerHTML = `
    <div class="sfe-toolbar-brand">
      Stock Forge+
      <span class="sfe-badge">POC</span>
    </div>
    <label class="sfe-toggle">
      <input type="checkbox" data-setting="scoreColumn" ${settings.scoreColumn ? "checked" : ""} />
      Score column
    </label>
    <label class="sfe-toggle">
      <input type="checkbox" data-setting="rowHighlighting" ${settings.rowHighlighting ? "checked" : ""} />
      Highlights
    </label>
    <label class="sfe-toggle">
      <input type="checkbox" data-setting="compactMode" ${settings.compactMode ? "checked" : ""} />
      Compact
    </label>
    <div class="sfe-field">
      Min ROI %
      <input type="number" data-setting="minRoi" value="${settings.minRoi}" min="0" step="10" />
    </div>
    <div class="sfe-field">
      Min POP %
      <input type="number" data-setting="minPop" value="${settings.minPop}" min="0" max="100" step="5" />
    </div>
    ${
      callbacks.hideConnectionIndicators
        ? ""
        : `<div class="sfe-toolbar-end">
      ${callbacks.stockForgeAuthCheck ? stockForgeAuthToolbarHtml() : ""}
      ${proxyHealthIndicatorHtml()}
    </div>`
    }
  `;

  const updateFromDom = () => {
    const next: EnhancementSettings = {
      scoreColumn:
        root.querySelector<HTMLInputElement>('[data-setting="scoreColumn"]')?.checked ?? true,
      rowHighlighting:
        root.querySelector<HTMLInputElement>('[data-setting="rowHighlighting"]')?.checked ?? true,
      compactMode:
        root.querySelector<HTMLInputElement>('[data-setting="compactMode"]')?.checked ?? false,
      minRoi: Number(
        root.querySelector<HTMLInputElement>('[data-setting="minRoi"]')?.value ?? settings.minRoi,
      ),
      minPop: Number(
        root.querySelector<HTMLInputElement>('[data-setting="minPop"]')?.value ?? settings.minPop,
      ),
    };
    callbacks.onSettingsChange(next);
  };

  root.querySelectorAll("input").forEach((input) => {
    input.addEventListener("change", updateFromDom);
    input.addEventListener("input", updateFromDom);
  });

  if (!callbacks.hideConnectionIndicators) {
    root.querySelector('[data-action="proxy-health"]')?.addEventListener("click", () => {
      openProxySettingsModal();
    });

    const stopPolling = startProxyHealthPolling((result) => applyProxyHealth(root, result));
    let stopAuth: (() => void) | undefined;
    if (callbacks.stockForgeAuthCheck) {
      const check = (): Promise<StockForgeConnectionResult> =>
        checkStockForgeConnection(callbacks.stockForgeAuthCheck!);
      stopAuth = bindStockForgeAuthToolbar(root, check);
    }
    (
      root as HTMLElement & { __sfeStopHealth?: () => void; __sfeStopAuth?: () => void }
    ).__sfeStopHealth = () => {
      stopPolling();
      stopAuth?.();
    };
  }

  return root;
}

function statusLabel(status: ProxyHealthStatus): string {
  switch (status) {
    case "ok":
      return "OK";
    case "error":
      return "Down";
    case "checking":
      return "Checking…";
    default:
      return "Unknown";
  }
}

function setHoverTip(el: HTMLElement, tip: string): void {
  el.title = tip;
  el.setAttribute("data-tip", tip);
}

let latestProxyHealth: ProxyHealthResult = {
  status: "unknown",
  message: "API proxy",
};

export function getProxyHealthResult(): ProxyHealthResult {
  return latestProxyHealth;
}

export function applyProxyHealth(toolbar: HTMLElement, result: ProxyHealthResult): void {
  latestProxyHealth = result;
  const dot = toolbar.querySelector<HTMLElement>("[data-proxy-dot]");
  const msg = toolbar.querySelector<HTMLElement>("[data-proxy-msg]");
  if (dot) dot.dataset.status = result.status;
  if (msg) msg.textContent = statusLabel(result.status);
  const btn = toolbar.querySelector<HTMLElement>("[data-action='proxy-health']");
  if (btn) setHoverTip(btn, `API proxy: ${result.message} — click to configure`);
}

export function applyEnhancements(settings: EnhancementSettings): void {
  const table = findScreenerTable();
  if (!table) return;

  if (settings.scoreColumn) {
    injectScoreColumn(table);
  } else {
    removeScoreColumn(table);
  }

  if (settings.rowHighlighting) {
    applyRowHighlights(table, settings.minRoi, settings.minPop);
  } else {
    clearRowHighlights(table);
    syncNoReportRowMarkers(table);
  }

  applyCompactMode(table, settings.compactMode);
  syncRubricBandFilter();
}

export function mountToolbar(
  container: HTMLElement,
  settings: EnhancementSettings,
  onChange: (settings: EnhancementSettings) => void,
  opts: {
    stockForgeAuthCheck?: () => Promise<{ account_id?: string }>;
    hideConnectionIndicators?: boolean;
  } = {},
): HTMLElement {
  const toolbar = createToolbar(settings, {
    onSettingsChange: onChange,
    stockForgeAuthCheck: opts.stockForgeAuthCheck,
    hideConnectionIndicators: opts.hideConnectionIndicators,
  });
  container.prepend(toolbar);
  return toolbar;
}
